import {
  ContactFormActionsContext,
  ContactFormDataContext,
  useContactFormActionsFromContext,
  useContactFormDataFromContext,
} from 'src/SharedCommonContext/ContactFormContext';
import { USER_ROLES } from 'src/common/constants';
import Toggle from 'src/components/Toggle';
import Input from 'src/components/Input';
import { IUserSuite } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import { SuiteListItemContactForm } from 'src/components/ContactForm/types';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import './contact-options.scss';

interface ContactOptionsProps {
  formType: string;
  pageUsedFrom?: string;
}

interface IndividualFoundSuite {
  id: string;
  name: string;
  propertyId: string;
  propertyName: string;
}

const ContactOptions = ({ formType, pageUsedFrom }: ContactOptionsProps) => {
  const { contactFormData }: ContactFormDataContext =
    useContactFormDataFromContext();

  const { setContactFormData }: ContactFormActionsContext =
    useContactFormActionsFromContext();

  const user: any = useSelector(selectAuthenticatedUser);
  const loggedInUserRole: any = user?.roles?.[0]?.role?.name;

  const showCalendarSection: boolean =
    contactFormData.userRole === 'Service Provider' ||
    contactFormData.userRole === 'Property Developer';

  const showPrimaryContactToggle: boolean =
    contactFormData.userRole === 'Occupant' ||
    contactFormData.userRole === 'Home Owner';

  const hasPermissionToEditAccess: boolean =
    loggedInUserRole === 'Admin' ||
    loggedInUserRole === 'Property Developer';

  const isRelevantForUserRole: boolean =
    contactFormData?.userRole?.toUpperCase() === USER_ROLES.OCCUPANT.toUpperCase() ||
    contactFormData?.userRole?.toUpperCase() === USER_ROLES.HOME_OWNER.toUpperCase() ||
    contactFormData?.userRole?.toUpperCase() === USER_ROLES.REALTOR.toUpperCase();

  const showDashboardAccessToggle: boolean = hasPermissionToEditAccess && isRelevantForUserRole;

  const isFromContactsPage: boolean = pageUsedFrom === 'Contacts';

  if (
    !showCalendarSection &&
    !showPrimaryContactToggle &&
    !showDashboardAccessToggle
  ) {
    return null;
  }

  const updateDesignated = (value: boolean) => {
    setContactFormData({ designated: value });

    if (value) {
      setContactFormData({
        isPrimaryContact: false,
        sendActivationEmail: false,
      });
    }
  };

  // Type guard to check if a suite is IUserSuite
  const isIUserSuite = (
    suite: SuiteListItemContactForm | IUserSuite
  ): suite is IUserSuite => {
    return 'suiteId' in suite && 'suiteName' in suite;
  };

  // Get all suites for the contact with their display information
  const getSuitesList = (): IndividualFoundSuite[] => {
    const suites: IndividualFoundSuite[] = [];

    // Check userSuites first (primary source with propertyId)
    if (contactFormData.userSuites && contactFormData.userSuites.length > 0) {
      contactFormData.userSuites.forEach((userSuite: IUserSuite) => {
        suites.push({
          id: userSuite.suiteId,
          name: userSuite.suiteName || 'Suite',
          propertyId: userSuite.propertyId,
          propertyName: userSuite.propertyName,
        });
      });
    }
    // Fallback to Suites if userSuites is not available
    else if (contactFormData.Suites && contactFormData.Suites.length > 0) {
      contactFormData.Suites.forEach(
        (suite: SuiteListItemContactForm | IUserSuite) => {
          if (isIUserSuite(suite)) {
            suites.push({
              id: suite.suiteId,
              name: suite.suiteName || 'Suite',
              propertyId: suite.propertyId,
              propertyName: suite.propertyName,
            });
          } else {
            suites.push({
              id: suite.Id,
              name: suite.name || 'Suite',
              propertyId: suite.propertyId,
              propertyName: undefined,
            });
          }
        }
      );
    }

    return suites;
  };

  const suitesList: IndividualFoundSuite[] = getSuitesList();

  return (
    <div className="contact-options">
      {showCalendarSection && (
        <div className="contact-options-calendar">
          <Input
            value={contactFormData.calendarLink ?? ''}
            onChange={(e: any) => {
              setContactFormData({ calendarLink: e.target.value });
            }}
            label="Your Calendaring Link"
            placeholder="Calendar Link"
            block
          />
        </div>
      )}
      {contactFormData.userRole === 'Home Owner' &&
        isFromContactsPage &&
        suitesList.length > 0 && (
          <div className="contact-options-homeowner-callout">
            <div className="contact-options-homeowner-callout-text">
              Looking for the Primary Homeowner toggles? These settings can be
              managed on a per-suite basis in the <strong>People tab</strong>{' '}
              within the <strong>suite details</strong> page. Click on a suite
              below to navigate there:
            </div>
            <div className="contact-options-suite-chips">
              {suitesList.map((suite: IndividualFoundSuite) => (
                <a
                  key={suite.id}
                  className="contact-options-suite-chip"
                  href={`/app/suites/${suite.propertyId}&${suite.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {suite.propertyName
                    ? `${suite.propertyName} - ${suite.name}`
                    : suite.name}
                </a>
              ))}
            </div>
          </div>
        )}
      {contactFormData.userRole === 'Home Owner' && formType !== 'Existing' && (
        <Toggle
          label="Inspection Designate"
          value={contactFormData.designated}
          onToggle={() => updateDesignated(!contactFormData.designated)}
        />
      )}
      {contactFormData.userRole === 'Home Owner' && !isFromContactsPage && (
        <div className="contact-options-primary-toggle">
          <Toggle
            label={`Make ${contactFormData?.userRole} the primary contact for the this suite`}
            value={contactFormData.isPrimaryContact}
            onToggle={() =>
              setContactFormData({
                isPrimaryContact: !contactFormData.isPrimaryContact,
              })
            }
          />
        </div>
      )}
      {showDashboardAccessToggle && (
        <div className="contact-options-dashboard-access">
          <Toggle
            label={`The ${contactFormData?.userRole} can only view the dashboard content`}
            value={contactFormData.isDashboardAccess}
            onToggle={() =>
              setContactFormData({
                isDashboardAccess: !contactFormData.isDashboardAccess,
              })
            }
          />
        </div>
      )}
    </div>
  );
};

export default ContactOptions;
